import { Button, Title } from "components/Shared";
import { Input, Label } from "components/Inputs";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Select from "react-select";
// @ts-ignore
import validate from "validate.js";
import { translate } from "lib";

const constraints = { name: { presence: true } };

// Keep in sync with Template::AMENDMENT_TYPES (tcp-api). Drives (Property,
// LeaseType, AmendmentType) template matching at project-creation time.
const AMENDMENT_TYPES = [
	"Original Lease",
	"Renewal",
	"Expansion",
	"Contraction",
	"Relocation",
	"Termination"
];

// Keep in sync with Template::LEASE_TYPES (tcp-api). Drives (Property, LeaseType)
// template matching at project-creation time.
const LEASE_TYPES = ["flagship", "fandb", "lineshop", "fastfood", "anchor", "minimajr"];

const BLANK_OPTION = { value: "", label: "None" };

interface ModalProps {
	id?: string;
	name?: string;
	property?: tcpinpoint.Property;
	amendment_type?: string;
	lease_types?: string[];
	onUpdate?: any;
	isOpen?: boolean;
	onRequestClose?: () => any;
}

const TemplateModal = (props: ModalProps) => {
	const { id, onUpdate, onRequestClose, isOpen } = props;
	const [name, setName] = useState(props.name);
	const [propertyId, setPropertyId] = useState(_.get(props, "property.id", "") as string);
	const [amendmentType, setAmendmentType] = useState(props.amendment_type || "");
	const [leaseTypes, setLeaseTypes] = useState<string[]>(props.lease_types || []);
	const [errors, setErrors] = useState<any>({});
	const [propertyOptions, setPropertyOptions] = useState([BLANK_OPTION] as any[]);
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(new Datasource({ mainModelName: "property", renderTrigger }));

	// re-seed state whenever the modal reopens with a (possibly updated) template
	const {
		id: propId,
		name: propName,
		property: propProperty,
		amendment_type: propAmendmentType,
		lease_types: propLeaseTypes
	} = props;
	useEffect(() => {
		setName(propName);
		setPropertyId(_.get(propProperty, "id", "") as string);
		setAmendmentType(propAmendmentType || "");
		setLeaseTypes(propLeaseTypes || []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [propId, propName, propProperty, propAmendmentType, JSON.stringify(propLeaseTypes)]);

	// receive admin properties and build dropdown options
	useEffect(() => {
		const sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			const options = _.map(response.normalizedMainModelResponse, (property: any) => ({
				value: property.id,
				label: property.name
			}));
			setPropertyOptions([BLANK_OPTION, ...options]);
		});
		return () => sub.unsubscribe();
	}, [datasource]);

	// fetch only properties the current user administers
	useEffect(() => {
		datasource.get("v2", "properties/index-admin-properties", {
			queryStringParameters: { per_page: 1000 }
		});
	}, [datasource]);

	const amendmentTypeOptions = [BLANK_OPTION, ...AMENDMENT_TYPES.map((t) => ({ value: t, label: t }))];
	const leaseTypeOptions = LEASE_TYPES.map((t) => ({ value: t, label: t }));
	const selectedLeaseTypeOptions = leaseTypeOptions.filter((o) => leaseTypes.includes(o.value));

	// F-1 lock: the property is only permanently locked once it has been PERSISTED
	// (i.e. it arrives via `props.property`). A property the user picks in-session
	// but hasn't saved yet must stay editable so they can correct a mis-selection
	// before clicking Ok. Keying off the prop (not the `propertyId` state, which
	// also updates on local selection) is what keeps the fresh dropdown editable.
	const isPropertyLocked = !!_.get(props, "property.id", "");

	const onSubmit = () => {
		const validationErrors = validate({ name }, constraints);
		if (!validationErrors) {
			!!onUpdate &&
				onUpdate({
					id,
					name,
					property_id: propertyId === "" ? null : propertyId,
					amendment_type: amendmentType === "" ? null : amendmentType,
					lease_types: leaseTypes
				});
			!!onRequestClose && onRequestClose();
		} else {
			setErrors(validationErrors);
		}
	};

	return (
		<Modal
			ariaHideApp={false}
			isOpen={isOpen!}
			onRequestClose={onRequestClose}
			className="template-modal"
			contentLabel="template"
		>
			<header>
				<div className="modal-header-body">
					<Title>{translate("edit_template_modal_header")}</Title>
				</div>
				<button type="button" className="close" aria-label="Close" onClick={onRequestClose}>
					<span aria-hidden="true">×</span>
				</button>
			</header>
			<section>
				<Input
					defaultState={{ name, errors }}
					required={constraints}
					name="name"
					onChange={(value: string) => setName(value)}
				/>
			</section>
			<section>
				<Input
					// Once a property has been PERSISTED it becomes permanently locked (both
					// here as UX and server-side per api-contract.md §1.2). The shared Input's
					// `disabled` branch renders `defaultState[name]` as static text, so when
					// locked we feed it the property NAME — taken DIRECTLY from the template's
					// `property` association (loaded with the template, so no extra fetch is
					// needed and the UUID never flashes). Only if that name is somehow missing
					// do we fall back to the async propertyOptions lookup, and never to the raw
					// UUID. While unlocked, we feed the raw `propertyId` (uuid) so the dropdown
					// matches its current selection. `onSubmit` reads the `propertyId` state
					// regardless, so the id is what actually gets saved.
					defaultState={{
						property_id: isPropertyLocked
							? _.get(props, "property.name", _.get(_.find(propertyOptions, { value: propertyId }), "label", ""))
							: propertyId
					}}
					name="property_id"
					label="Property"
					options={propertyOptions}
					disabled={isPropertyLocked}
					// Locked properties render as a greyed-out, non-interactive field
					// (see `.form-group-property-id.is-locked` in _form.scss).
					className={isPropertyLocked ? "is-locked" : undefined}
					onChange={(value: string) => setPropertyId(value)}
				/>
			</section>
			<section>
				<div className="form-group form-group-lease-types">
					<Label name="lease_types" label="Lease Type">
						<Select
							isMulti
							name="lease_types"
							className="react-select"
							classNamePrefix="react-select"
							placeholder="Select one or more lease types"
							menuPortalTarget={typeof document !== "undefined" ? document.body : null}
							menuPosition="fixed"
							styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
							value={selectedLeaseTypeOptions}
							options={leaseTypeOptions}
							onChange={(selected: any) => setLeaseTypes(_.map(selected || [], "value"))}
						/>
					</Label>
				</div>
			</section>
			<section>
				<Input
					defaultState={{ amendment_type: amendmentType }}
					name="amendment_type"
					label="Amendment Type"
					options={amendmentTypeOptions}
					onChange={(value: string) => setAmendmentType(value)}
				/>
			</section>
			<footer>
				<Button onClick={() => onSubmit()} />
			</footer>
		</Modal>
	);
};

export default TemplateModal;
